import { scopeProjectRef } from "../environment/scoped.ts";
import type { ScopedProjectRef } from "@heydex/contracts";

import type { EnvironmentProject } from "./models.ts";
import { normalizeProjectPathForComparison } from "./projects.ts";

function derivePhysicalProjectKeyFromPath(environmentId: string, cwd: string): string {
  return `${environmentId}:${normalizeProjectPathForComparison(cwd)}`;
}

/** One key per folder on an environment: projects that share a folder are one group. */
export function derivePhysicalProjectKey(
  project: Pick<EnvironmentProject, "environmentId" | "workspaceRoot">,
): string {
  return derivePhysicalProjectKeyFromPath(project.environmentId, project.workspaceRoot);
}

export interface ProjectGroupMember<TProject extends EnvironmentProject = EnvironmentProject> {
  readonly physicalProjectKey: string;
  readonly project: TProject;
}

export interface ProjectGroup<TProject extends EnvironmentProject = EnvironmentProject> {
  readonly key: string;
  readonly label: string;
  readonly representative: TProject;
  readonly members: ReadonlyArray<ProjectGroupMember<TProject>>;
  readonly memberProjectRefs: ReadonlyArray<ScopedProjectRef>;
}

function projectFreshnessTime(project: EnvironmentProject): number {
  const updatedAtTime = Date.parse(project.updatedAt);
  if (Number.isFinite(updatedAtTime)) {
    return updatedAtTime;
  }
  const createdAtTime = Date.parse(project.createdAt);
  return Number.isFinite(createdAtTime) ? createdAtTime : 0;
}

function isFresher<TProject extends EnvironmentProject>(
  existing: TProject,
  candidate: TProject,
): boolean {
  const freshnessDelta = projectFreshnessTime(candidate) - projectFreshnessTime(existing);
  return freshnessDelta > 0 || (freshnessDelta === 0 && candidate.id > existing.id);
}

/**
 * Groups projects by folder. The freshest project of a folder represents it; every
 * project in it stays a member ref, since each remains a navigation target.
 */
export function buildProjectGroups<TProject extends EnvironmentProject>(input: {
  readonly projects: ReadonlyArray<TProject>;
}): ReadonlyArray<ProjectGroup<TProject>> {
  const groups = new Map<string, { winner: TProject; refs: ScopedProjectRef[] }>();
  for (const project of input.projects) {
    const key = derivePhysicalProjectKey(project);
    const ref = scopeProjectRef(project.environmentId, project.id);
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, { winner: project, refs: [ref] });
      continue;
    }
    if (isFresher(existing.winner, project)) existing.winner = project;
    if (!existing.refs.some((candidate) => candidate.projectId === project.id)) {
      existing.refs.push(ref);
    }
  }
  return Array.from(groups, ([key, { winner, refs }]) => ({
    key,
    label: winner.title,
    representative: winner,
    members: [{ physicalProjectKey: key, project: winner }],
    memberProjectRefs: refs,
  }));
}
